import React, { useState } from 'react';
import { X, Download, Search, Menu, ChevronRight, Home, BarChart3, Users, Building2, Calendar, UserCheck, Zap, LayoutDashboard, ListChecks, ClipboardList, CheckSquare, Receipt, TrendingUp, CreditCard, FileText, Calculator, User, Target, Cpu, Bot, Mail, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardContent } from './ui/card';
import { useAuth } from '../contexts/AuthContext';

interface UserManualProps {
  isOpen: boolean;
  onClose: () => void;
}

const SimpleUserManual: React.FC<UserManualProps> = ({ isOpen, onClose }) => {
  const { profile } = useAuth();
  const [selectedSection, setSelectedSection] = useState<string>('getting-started');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Determine user function based on role and function
  const userFunction = profile?.role === 'Vendor' ? 'Vendor' :
    profile?.role === 'Client' ? 'Client' :
      (profile?.function || 'Founder');

  const generatePDF = () => {
    // Create a new window with just the user manual content
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>CupShup AI User Manual - Complete Guide</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              line-height: 1.6;
              color: #1d2121;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
              background: white;
            }
            .header {
              text-align: center;
              margin-bottom: 40px;
              border-bottom: 2px solid #0da5e1;
              padding-bottom: 20px;
            }
            .header h1 {
              color: #1d2121;
              font-size: 28px;
              margin: 0 0 10px 0;
            }
            .header p {
              color: #666;
              font-size: 16px;
              margin: 0;
            }
            .toc {
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 40px;
            }
            .toc h2 {
              color: #1d2121;
              font-size: 20px;
              margin: 0 0 15px 0;
              border-bottom: 1px solid #e2e8f0;
              padding-bottom: 10px;
            }
            .toc ul {
              list-style: none;
              padding: 0;
              margin: 0;
            }
            .toc li {
              margin: 8px 0;
              padding-left: 20px;
              position: relative;
            }
            .toc li:before {
              content: "•";
              color: #0da5e1;
              position: absolute;
              left: 0;
            }
            .toc a {
              color: #1d2121;
              text-decoration: none;
              font-size: 14px;
            }
            .toc a:hover {
              color: #0da5e1;
            }
            .section-title {
              color: #1d2121;
              font-size: 24px;
              margin: 40px 0 20px 0;
              border-left: 4px solid #0da5e1;
              padding-left: 15px;
              page-break-before: always;
            }
            .section-title:first-of-type {
              page-break-before: avoid;
            }
            .section-description {
              color: #666;
              font-size: 16px;
              margin-bottom: 30px;
              line-height: 1.7;
            }
            .features-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
              gap: 20px;
              margin: 20px 0;
            }
            .feature-card {
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 20px;
              background: #f9fafb;
              page-break-inside: avoid;
            }
            .feature-title {
              color: #0da5e1;
              font-size: 18px;
              font-weight: 600;
              margin: 0 0 10px 0;
            }
            .feature-description {
              color: #666;
              font-size: 14px;
              line-height: 1.6;
              margin: 0;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #e5e7eb;
              text-align: center;
              color: #666;
              font-size: 12px;
            }
            @media print {
              body { margin: 0; padding: 15px; }
              .header { page-break-after: avoid; }
              .toc { page-break-after: avoid; }
              .section-title { page-break-before: always; }
              .section-title:first-of-type { page-break-before: avoid; }
              .feature-card { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>CupShup AI User Manual</h1>
            <p>Complete Guide for ${userFunction} Function</p>
          </div>
          
          <div class="toc">
            <h2>Table of Contents</h2>
            <ul>
              ${sections.map(section => `<li><a href="#${section.id}">${section.title}</a></li>`).join('')}
            </ul>
          </div>
          
          <div class="content">
            ${getPDFContent()}
          </div>
          
          <div class="footer">
            <p>Generated on ${new Date().toLocaleDateString()} | CupShup AI 3.0</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const getPDFContent = () => {
    // Generate content for ALL sections, not just the current one
    return sections.map(section => {
      // Check if we have role-based content for this section
      const roleBasedContent = getRoleBasedContent(section.id);
      if (roleBasedContent) {
        return `
          <h2 class="section-title" id="${section.id}">${roleBasedContent.title}</h2>
          <p class="section-description">${roleBasedContent.description}</p>
          
          <div class="features-grid">
            ${roleBasedContent.features.map(feature => `
              <div class="feature-card">
                <h4 class="feature-title">${feature.title}</h4>
                <p class="feature-description">${feature.description}</p>
              </div>
            `).join('')}
          </div>
        `;
      }

      // Fall back to default content
      switch (section.id) {
        case 'getting-started':
          return `
            <h2 class="section-title" id="getting-started">Getting Started with CupShup AI</h2>
            <p class="section-description">
              Welcome to CupShup AI 3.0, your comprehensive marketing agency management platform. 
              This guide will help you understand the platform's capabilities and get started with your daily workflow.
            </p>
            
            <h3>Platform Overview</h3>
            <p>The platform is organized into main sections accessible via the left sidebar. Each contains specific tools and features designed for different aspects of your business operations.</p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Dashboard & Analytics</h4>
                <p class="feature-description">Monitor key performance indicators, track business metrics, and analyze data trends.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Vendor Management</h4>
                <p class="feature-description">Manage your vendor network, track performance, and streamline vendor relationships.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Client Management</h4>
                <p class="feature-description">Maintain client relationships, track projects, and manage client communications.</p>
              </div>
            </div>
          `;

        case 'dashboard':
          return `
            <h2 class="section-title" id="dashboard">Dashboard & Analytics Hub</h2>
            <p class="section-description">
              Master your business metrics and analytics with comprehensive dashboards that provide real-time insights into your agency's performance.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Key Performance Indicators</h4>
                <p class="feature-description">Track revenue, profit margins, client satisfaction, and operational efficiency metrics.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Real-time Analytics</h4>
                <p class="feature-description">Monitor live data streams and get instant updates on business performance.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Custom Reports</h4>
                <p class="feature-description">Generate detailed reports tailored to your specific business needs and requirements.</p>
              </div>
            </div>
          `;

        case 'vendors':
          return `
            <h2 class="section-title" id="vendors">Vendor Management System</h2>
            <p class="section-description">
              Complete vendor lifecycle management with comprehensive tools for onboarding, performance tracking, and relationship management.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Vendor Onboarding</h4>
                <p class="feature-description">Streamlined onboarding process with automated workflows and document management.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Performance Tracking</h4>
                <p class="feature-description">Monitor vendor performance, quality metrics, and delivery timelines.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Communication Hub</h4>
                <p class="feature-description">Centralized communication platform for all vendor interactions and updates.</p>
              </div>
            </div>
          `;

        case 'clients':
          return `
            <h2 class="section-title" id="clients">Client Relationship Management</h2>
            <p class="section-description">
              Comprehensive client management system with advanced CRM capabilities, project tracking, and communication tools.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Client Profiles</h4>
                <p class="feature-description">Maintain detailed client profiles with contact information, preferences, and history.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Project Management</h4>
                <p class="feature-description">Track client projects, milestones, and deliverables with real-time updates.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Communication Tools</h4>
                <p class="feature-description">Integrated email, messaging, and notification systems for seamless client communication.</p>
              </div>
            </div>
          `;

        case 'activities':
          return `
            <h2 class="section-title" id="activities">Activity & Project Management</h2>
            <p class="section-description">
              Complete project lifecycle management with advanced planning, tracking, and collaboration tools.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Project Planning</h4>
                <p class="feature-description">Create and manage marketing activities with detailed timelines and budgets.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Vendor Assignment</h4>
                <p class="feature-description">Assign vendors to specific activities with clear instructions and deliverables.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Progress Tracking</h4>
                <p class="feature-description">Monitor real-time progress and completion status across all activities.</p>
              </div>
            </div>
          `;

        case 'operations':
          return `
            <h2 class="section-title" id="operations">Operations Management</h2>
            <p class="section-description">
              Streamline operational workflows, manage processes, and optimize business operations with comprehensive tools and analytics.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Process Management</h4>
                <p class="feature-description">Design, implement, and monitor operational processes with workflow automation and performance tracking.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Operational Analytics</h4>
                <p class="feature-description">Track operational KPIs, efficiency metrics, and performance indicators with real-time dashboards.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Workflow Optimization</h4>
                <p class="feature-description">Optimize workflows, reduce bottlenecks, and improve operational efficiency with data-driven insights.</p>
              </div>
            </div>
          `;

        case 'mapped-activities':
          return `
            <h2 class="section-title" id="mapped-activities">Mapped Activities</h2>
            <p class="section-description">
              Track and manage mapped activities with comprehensive visibility into project progress, resource allocation, and performance metrics.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Activity Mapping</h4>
                <p class="feature-description">Map activities to specific projects, clients, and vendors with detailed tracking and status updates.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Progress Tracking</h4>
                <p class="feature-description">Monitor activity progress, completion rates, and performance metrics with real-time updates.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Resource Allocation</h4>
                <p class="feature-description">Allocate resources, assign team members, and manage workload distribution across mapped activities.</p>
              </div>
            </div>
          `;

        case 'mapped-tasks':
          return `
            <h2 class="section-title" id="mapped-tasks">Mapped Tasks</h2>
            <p class="section-description">
              Organize and track mapped tasks with detailed task management, assignment tracking, and completion monitoring.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Task Organization</h4>
                <p class="feature-description">Organize tasks by priority, category, and project with advanced filtering and sorting capabilities.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Task Assignment</h4>
                <p class="feature-description">Assign tasks to team members, set deadlines, and track completion status with notifications.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Progress Monitoring</h4>
                <p class="feature-description">Monitor task progress, identify bottlenecks, and generate performance reports for better management.</p>
              </div>
            </div>
          `;

        case 'todo':
          return `
            <h2 class="section-title" id="todo">Todo Management</h2>
            <p class="section-description">
              Personal and team task management with priority tracking, deadline management, and collaborative task organization.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Personal Tasks</h4>
                <p class="feature-description">Create, organize, and track personal tasks with priority levels, due dates, and completion tracking.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Team Collaboration</h4>
                <p class="feature-description">Share tasks with team members, assign responsibilities, and collaborate on project deliverables.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Deadline Management</h4>
                <p class="feature-description">Set deadlines, receive reminders, and track overdue tasks with automated notifications.</p>
              </div>
            </div>
          `;

        case 'automations':
          return `
            <h2 class="section-title" id="automations">Automation Hub</h2>
            <p class="section-description">
              Automate workflows and processes to increase efficiency, reduce manual work, and streamline business operations.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Workflow Automation</h4>
                <p class="feature-description">Create automated workflows for repetitive tasks, approvals, and notifications to save time and reduce errors.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Scheduled Tasks</h4>
                <p class="feature-description">Schedule automated tasks, reports, and notifications to run at specific times or intervals.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Performance Analytics</h4>
                <p class="feature-description">Monitor automation performance, track efficiency gains, and optimize workflows based on data insights.</p>
              </div>
            </div>
          `;

        case 'invoicing':
          return `
            <h2 class="section-title" id="invoicing">Invoicing System</h2>
            <p class="section-description">
              Complete billing and invoicing management with automated invoice generation, payment tracking, and financial reporting.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Invoice Generation</h4>
                <p class="feature-description">Create professional invoices with customizable templates, automated calculations, and tax handling.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Payment Tracking</h4>
                <p class="feature-description">Track invoice payments, manage overdue accounts, and send automated payment reminders.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Financial Reports</h4>
                <p class="feature-description">Generate comprehensive financial reports, revenue analytics, and cash flow projections.</p>
              </div>
            </div>
          `;

        case 'pl':
          return `
            <h2 class="section-title" id="pl">Profit & Loss (P&L)</h2>
            <p class="section-description">
              Comprehensive financial performance analysis with detailed P&L statements, revenue tracking, and expense management.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Revenue Analysis</h4>
                <p class="feature-description">Track revenue streams, analyze growth trends, and identify profitable business segments.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Expense Management</h4>
                <p class="feature-description">Categorize and track expenses, monitor cost centers, and optimize spending patterns.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Profitability Reports</h4>
                <p class="feature-description">Generate detailed P&L statements, margin analysis, and profitability reports for informed decision making.</p>
              </div>
            </div>
          `;

        case 'payment-requests':
          return `
            <h2 class="section-title" id="payment-requests">Payment Requests</h2>
            <p class="section-description">
              Manage payment requests and approvals with streamlined workflows, approval chains, and financial tracking.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Request Management</h4>
                <p class="feature-description">Create, submit, and track payment requests with detailed documentation and approval workflows.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Approval Workflows</h4>
                <p class="feature-description">Configure approval chains, set spending limits, and automate approval processes based on amount and category.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Financial Tracking</h4>
                <p class="feature-description">Track payment history, monitor spending patterns, and generate financial reports for better budget management.</p>
              </div>
            </div>
          `;

        case 'leads':
          return `
            <h2 class="section-title" id="leads">Lead Management</h2>
            <p class="section-description">
              Track and convert leads to clients with comprehensive lead management, pipeline tracking, and conversion analytics.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Lead Capture</h4>
                <p class="feature-description">Capture leads from multiple sources, organize contact information, and track lead sources and quality.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Pipeline Management</h4>
                <p class="feature-description">Manage sales pipeline, track lead progression, and monitor conversion rates at each stage.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Conversion Analytics</h4>
                <p class="feature-description">Analyze lead conversion rates, identify successful strategies, and optimize sales processes.</p>
              </div>
            </div>
          `;

        case 'hrms':
          return `
            <h2 class="section-title" id="hrms">Human Resource Management System</h2>
            <p class="section-description">
              Comprehensive HR management with attendance tracking, performance management, and employee lifecycle management.
            </p>
            
            <div class="features-grid">
              <div class="feature-card">
                <h4 class="feature-title">Attendance Tracking</h4>
                <p class="feature-description">Track employee attendance, working hours, and generate attendance reports with real-time monitoring.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Performance Management</h4>
                <p class="feature-description">Set KRAs, track performance metrics, and conduct regular performance reviews with detailed analytics.</p>
              </div>
              <div class="feature-card">
                <h4 class="feature-title">Employee Management</h4>
                <p class="feature-description">Manage employee profiles, roles, permissions, and organizational hierarchy with complete lifecycle tracking.</p>
              </div>
            </div>
          `;

        default:
          return `
            <h2 class="section-title">${section.title}</h2>
            <p class="section-description">${section.description}</p>
            <p>This section contains comprehensive information about ${section.title.toLowerCase()}. Please refer to the online manual for detailed features and functionality.</p>
          `;
      }
    }).join('');
  };

  // Role-based sections configuration
  const getSectionsForRole = (userFunction: string) => {
    const baseSections = [
      {
        id: 'getting-started',
        title: '1. Getting Started with CupShup AI',
        icon: Home,
        description: 'Welcome guide and platform overview'
      },
      {
        id: 'dashboard',
        title: '2. Dashboard & Analytics Hub',
        icon: LayoutDashboard,
        description: 'Master your business metrics and analytics'
      }
    ];

    if (userFunction === 'Sales') {
      return [
        ...baseSections,
        {
          id: 'vendors',
          title: '3. Vendor Management',
          icon: Users,
          description: 'Manage vendor relationships and coordination'
        },
        {
          id: 'clients',
          title: '4. Client Relationship Management',
          icon: Building2,
          description: 'Comprehensive client management system'
        },
        {
          id: 'activities',
          title: '5. Sales Activities & Projects',
          icon: Calendar,
          description: 'Manage sales activities and client projects'
        },
        {
          id: 'mapped-activities',
          title: '6. Mapped Activities',
          icon: ListChecks,
          description: 'Track and manage mapped activities'
        },
        {
          id: 'mapped-tasks',
          title: '7. Mapped Tasks',
          icon: ClipboardList,
          description: 'Organize and track mapped tasks'
        },
        {
          id: 'todo',
          title: '8. Sales Task Management',
          icon: CheckSquare,
          description: 'Manage sales tasks and follow-ups'
        },
        {
          id: 'automations',
          title: '9. Sales Automation',
          icon: Zap,
          description: 'Automate sales workflows and processes'
        },
        {
          id: 'invoicing',
          title: '10. Sales Invoicing & Billing',
          icon: Receipt,
          description: 'Manage client invoicing and billing processes'
        },
        {
          id: 'payment-requests',
          title: '11. Payment Tracking',
          icon: CreditCard,
          description: 'Track client payments and outstanding amounts'
        },
        {
          id: 'leads',
          title: '12. Lead Management & Pipeline',
          icon: FileText,
          description: 'Track and convert leads to clients'
        },
        {
          id: 'hrms',
          title: '13. Human Resource Management',
          icon: UserCheck,
          description: 'Complete HR system with attendance tracking'
        }
      ];
    }

    if (userFunction === 'Operations' || userFunction === 'Operation') {
      return [
        ...baseSections,
        {
          id: 'vendors',
          title: '3. Vendor Management',
          icon: Users,
          description: 'Manage vendor relationships and operations'
        },
        {
          id: 'activities',
          title: '4. Operational Activities',
          icon: Calendar,
          description: 'Manage operational activities and workflows'
        },
        {
          id: 'operations',
          title: '5. Operations Management',
          icon: Calculator,
          description: 'Streamline operational workflows and processes'
        },
        {
          id: 'mapped-activities',
          title: '6. Mapped Activities',
          icon: ListChecks,
          description: 'Track and manage mapped activities'
        },
        {
          id: 'mapped-tasks',
          title: '7. Mapped Tasks',
          icon: ClipboardList,
          description: 'Organize and track mapped tasks'
        },
        {
          id: 'todo',
          title: '8. Operational Task Management',
          icon: CheckSquare,
          description: 'Manage operational tasks and workflows'
        },
        {
          id: 'payment-requests',
          title: '9. Payment Request Management',
          icon: CreditCard,
          description: 'Manage payment requests and approvals'
        },
        {
          id: 'hrms',
          title: '10. Human Resource Management',
          icon: UserCheck,
          description: 'Complete HR system with attendance tracking'
        }
      ];
    }

    if (userFunction === 'Finance') {
      return [
        ...baseSections,
        {
          id: 'vendors',
          title: '3. Vendor Management',
          icon: Users,
          description: 'Manage vendor relationships and financial coordination'
        },
        {
          id: 'clients',
          title: '4. Client Financial Management',
          icon: Building2,
          description: 'Manage client financial relationships and billing'
        },
        {
          id: 'activities',
          title: '5. Financial Activities & Projects',
          icon: Calendar,
          description: 'Track financial activities and project costs'
        },
        {
          id: 'mapped-activities',
          title: '6. Mapped Financial Activities',
          icon: ListChecks,
          description: 'Track and manage mapped financial activities'
        },
        {
          id: 'mapped-tasks',
          title: '7. Mapped Financial Tasks',
          icon: ClipboardList,
          description: 'Organize and track mapped financial tasks'
        },
        {
          id: 'todo',
          title: '8. Financial Task Management',
          icon: CheckSquare,
          description: 'Manage financial tasks and workflows'
        },
        {
          id: 'invoicing',
          title: '9. Invoicing & Billing Management',
          icon: Receipt,
          description: 'Comprehensive invoicing and billing system'
        },
        {
          id: 'payment-requests',
          title: '10. Payment Request Management',
          icon: CreditCard,
          description: 'Manage payment requests and approvals'
        },
        {
          id: 'hrms',
          title: '11. Human Resource Management',
          icon: UserCheck,
          description: 'Complete HR system with attendance tracking'
        }
      ];
    }

    if (userFunction === 'Marketing Intern') {
      return [
        ...baseSections,
        {
          id: 'vendors',
          title: '3. Vendor Management',
          icon: Users,
          description: 'Manage vendor relationships and marketing coordination'
        },
        {
          id: 'clients',
          title: '4. Client Marketing Management',
          icon: Building2,
          description: 'Manage client marketing relationships and campaigns'
        },
        {
          id: 'activities',
          title: '5. Marketing Activities & Campaigns',
          icon: Calendar,
          description: 'Track marketing activities and campaign management'
        },
        {
          id: 'mapped-activities',
          title: '6. Mapped Marketing Activities',
          icon: ListChecks,
          description: 'Track and manage mapped marketing activities'
        },
        {
          id: 'mapped-tasks',
          title: '7. Mapped Marketing Tasks',
          icon: ClipboardList,
          description: 'Organize and track mapped marketing tasks'
        },
        {
          id: 'todo',
          title: '8. Marketing Task Management',
          icon: CheckSquare,
          description: 'Manage marketing tasks and campaign workflows'
        },
        {
          id: 'payment-requests',
          title: '9. Payment Request Management',
          icon: CreditCard,
          description: 'Manage payment requests and approvals'
        },
        {
          id: 'leads',
          title: '10. Lead Management & Pipeline',
          icon: FileText,
          description: 'Track and convert leads to clients'
        },
        {
          id: 'hrms',
          title: '11. Human Resource Management',
          icon: UserCheck,
          description: 'Complete HR system with attendance tracking'
        }
      ];
    }

    if (userFunction === 'Vendor' || userFunction === 'vendor') {
      return [
        {
          id: 'getting-started',
          title: '1. Getting Started with Vendor Portal',
          icon: Home,
          description: 'Welcome guide and profile setup for vendors'
        },
        {
          id: 'dashboard',
          title: '2. Vendor Dashboard',
          icon: LayoutDashboard,
          description: 'Monitor profile completion and project opportunities'
        },
        {
          id: 'activities',
          title: '3. Available Activities',
          icon: Calendar,
          description: 'Browse and apply for available projects'
        },
        {
          id: 'my-activities',
          title: '4. My Activities',
          icon: CheckSquare,
          description: 'Manage your assigned projects and deliverables'
        },
        {
          id: 'my-tasks',
          title: '5. My Tasks',
          icon: ClipboardList,
          description: 'Track and complete your assigned tasks'
        },
        {
          id: 'profile',
          title: '6. Profile Management',
          icon: User,
          description: 'Complete your vendor profile and showcase portfolio'
        }
      ];
    }

    if (userFunction === 'Client' || userFunction === 'client') {
      return [
        {
          id: 'getting-started',
          title: '1. Getting Started with Client Portal',
          icon: Home,
          description: 'Welcome guide and platform overview for clients'
        },
        {
          id: 'dashboard',
          title: '2. Client Dashboard',
          icon: LayoutDashboard,
          description: 'Your client dashboard and project overview'
        },
        {
          id: 'media-planning',
          title: '3. Media Planning',
          icon: Target,
          description: 'Plan and strategize your media campaigns'
        },
        {
          id: 'my-plans',
          title: '4. My Plans',
          icon: ClipboardList,
          description: 'Manage your marketing plans and strategies'
        },
        {
          id: 'activities',
          title: '5. All Activities',
          icon: ListChecks,
          description: 'View and track all your project activities'
        },
        {
          id: 'my-tasks',
          title: '6. All Tasks',
          icon: FileText,
          description: 'Monitor all tasks across your projects'
        },
        {
          id: 'ai-tools',
          title: '7. AI Tools',
          icon: Cpu,
          description: 'Access powerful AI-powered marketing tools'
        },
        {
          id: 'ai-agents',
          title: '8. AI Agents',
          icon: Bot,
          description: 'Interact with AI agents for marketing assistance'
        },
        {
          id: 'email-campaign',
          title: '9. Email Campaign',
          icon: Mail,
          description: 'Create and manage email marketing campaigns'
        },
        {
          id: 'whatsapp-campaign',
          title: '10. WhatsApp Campaign',
          icon: MessageSquare,
          description: 'Launch and track WhatsApp marketing campaigns'
        }
      ];
    }

    // Default sections for other roles (Founder, etc.)
    return [
      ...baseSections,
      {
        id: 'vendors',
        title: '3. Vendor Management System',
        icon: Users,
        description: 'Complete vendor lifecycle management'
      },
      {
        id: 'clients',
        title: '4. Client Relationship Management',
        icon: Building2,
        description: 'Comprehensive client management'
      },
      {
        id: 'activities',
        title: '5. Activity & Project Management',
        icon: Calendar,
        description: 'Complete project lifecycle management'
      },
      {
        id: 'operations',
        title: '6. Operations Management',
        icon: Calculator,
        description: 'Operational workflows and processes'
      },
      {
        id: 'mapped-activities',
        title: '7. Mapped Activities',
        icon: ListChecks,
        description: 'Track and manage mapped activities'
      },
      {
        id: 'mapped-tasks',
        title: '8. Mapped Tasks',
        icon: ClipboardList,
        description: 'Organize and track mapped tasks'
      },
      {
        id: 'todo',
        title: '9. Todo Management',
        icon: CheckSquare,
        description: 'Personal and team task management'
      },
      {
        id: 'automations',
        title: '10. Automation Hub',
        icon: Zap,
        description: 'Automate workflows and processes'
      },
      {
        id: 'invoicing',
        title: '11. Invoicing System',
        icon: Receipt,
        description: 'Complete billing and invoicing management'
      },
      {
        id: 'pl',
        title: '12. Profit & Loss (P&L)',
        icon: TrendingUp,
        description: 'Financial performance and analytics'
      },
      {
        id: 'payment-requests',
        title: '13. Payment Requests',
        icon: CreditCard,
        description: 'Manage payment requests and approvals'
      },
      {
        id: 'leads',
        title: '14. Lead Management',
        icon: FileText,
        description: 'Track and convert leads to clients'
      },
      {
        id: 'hrms',
        title: '15. Human Resource Management',
        icon: UserCheck,
        description: 'Complete HR system with attendance tracking'
      }
    ];
  };

  const sections = getSectionsForRole(userFunction);

  // Get role-based content for each section
  const getRoleBasedContent = (sectionId: string) => {
    if (userFunction === 'Operations' || userFunction === 'Operation') {
      switch (sectionId) {
        case 'getting-started':
          return {
            title: 'Welcome to CupShup AI - Operations Team',
            description: 'CupShup AI is your comprehensive operations management platform designed to help you streamline workflows, optimize processes, and ensure operational excellence. This guide will help you master the tools and features specifically designed for operations professionals.',
            features: [
              { title: 'Process Management', description: 'Design, implement, and monitor operational processes with workflow automation', icon: Calculator },
              { title: 'Vendor Operations', description: 'Manage vendor relationships and operational coordination', icon: Users },
              { title: 'Operational Analytics', description: 'Track operational KPIs and performance metrics', icon: BarChart3 }
            ]
          };
        case 'vendors':
          return {
            title: 'Vendor Management',
            description: 'Manage vendor relationships and coordinate operational activities with vendors to ensure smooth business operations.',
            features: [
              { title: 'Vendor Coordination', description: 'Coordinate with vendors for operational activities and deliverables', icon: Users },
              { title: 'Performance Monitoring', description: 'Monitor vendor performance and operational metrics', icon: BarChart3 },
              { title: 'Operational Workflows', description: 'Manage vendor-related operational workflows and processes', icon: Calendar }
            ]
          };
        case 'activities':
          return {
            title: 'Operational Activities',
            description: 'Manage operational activities, workflows, and processes to ensure efficient business operations.',
            features: [
              { title: 'Activity Planning', description: 'Plan and schedule operational activities and workflows', icon: Calendar },
              { title: 'Process Execution', description: 'Execute operational processes and monitor progress', icon: CheckSquare },
              { title: 'Workflow Management', description: 'Manage operational workflows and process optimization', icon: Calculator }
            ]
          };
        case 'operations':
          return {
            title: 'Operations Management',
            description: 'Streamline operational workflows, manage processes, and optimize business operations with comprehensive tools and analytics.',
            features: [
              { title: 'Process Management', description: 'Design, implement, and monitor operational processes with workflow automation and performance tracking', icon: Calculator },
              { title: 'Operational Analytics', description: 'Track operational KPIs, efficiency metrics, and performance indicators with real-time dashboards', icon: BarChart3 },
              { title: 'Workflow Optimization', description: 'Optimize workflows, reduce bottlenecks, and improve operational efficiency with data-driven insights', icon: TrendingUp }
            ]
          };
        case 'mapped-activities':
          return {
            title: 'Mapped Activities',
            description: 'Track and manage mapped activities with comprehensive visibility into operational progress, resource allocation, and performance metrics.',
            features: [
              { title: 'Activity Mapping', description: 'Map activities to specific operational processes, vendors, and workflows with detailed tracking', icon: ListChecks },
              { title: 'Progress Tracking', description: 'Monitor activity progress, completion rates, and operational performance metrics', icon: BarChart3 },
              { title: 'Resource Allocation', description: 'Allocate resources, assign team members, and manage workload distribution across mapped activities', icon: Users }
            ]
          };
        case 'mapped-tasks':
          return {
            title: 'Mapped Tasks',
            description: 'Organize and track mapped tasks with detailed task management, assignment tracking, and completion monitoring for operational efficiency.',
            features: [
              { title: 'Task Organization', description: 'Organize tasks by priority, category, and operational process with advanced filtering', icon: ClipboardList },
              { title: 'Task Assignment', description: 'Assign tasks to team members, set deadlines, and track completion status', icon: CheckSquare },
              { title: 'Progress Monitoring', description: 'Monitor task progress, identify bottlenecks, and generate operational performance reports', icon: BarChart3 }
            ]
          };
        case 'todo':
          return {
            title: 'Operational Task Management',
            description: 'Organize your operational tasks, set priorities, and manage daily operational workflows efficiently.',
            features: [
              { title: 'Task Organization', description: 'Organize tasks by priority, operational process, and deadline', icon: CheckSquare },
              { title: 'Workflow Management', description: 'Manage operational workflows and process-related tasks', icon: Calendar },
              { title: 'Performance Tracking', description: 'Track daily operational activities and efficiency metrics', icon: BarChart3 }
            ]
          };
        case 'payment-requests':
          return {
            title: 'Payment Request Management',
            description: 'Manage payment requests and approvals with streamlined workflows, approval chains, and operational tracking.',
            features: [
              { title: 'Request Management', description: 'Create, submit, and track payment requests with detailed documentation', icon: CreditCard },
              { title: 'Approval Workflows', description: 'Configure approval chains and automate approval processes for operational efficiency', icon: Calculator },
              { title: 'Operational Tracking', description: 'Track payment history and monitor operational spending patterns', icon: BarChart3 }
            ]
          };
        case 'hrms':
          return {
            title: 'Human Resource Management System',
            description: 'Comprehensive HR management with attendance tracking, performance management, and employee lifecycle management for operational teams.',
            features: [
              { title: 'Attendance Tracking', description: 'Track employee attendance, working hours, and generate operational reports', icon: UserCheck },
              { title: 'Performance Management', description: 'Set KRAs, track performance metrics, and conduct operational performance reviews', icon: BarChart3 },
              { title: 'Team Management', description: 'Manage team profiles, roles, and operational responsibilities', icon: Users }
            ]
          };
      }
    }

    if (userFunction === 'Sales') {
      switch (sectionId) {
        case 'getting-started':
          return {
            title: 'Welcome to CupShup AI - Sales Team',
            description: 'CupShup AI is your comprehensive sales management platform designed to help you excel in lead generation, client acquisition, and revenue growth. This guide will help you master the tools and features specifically designed for sales professionals.',
            features: [
              { title: 'Lead Management', description: 'Track and convert leads with advanced pipeline management', icon: FileText },
              { title: 'Client Relations', description: 'Build and maintain strong client relationships', icon: Building2 },
              { title: 'Sales Analytics', description: 'Monitor performance with real-time sales metrics', icon: BarChart3 }
            ]
          };
        case 'leads':
          return {
            title: 'Lead Management & Pipeline',
            description: 'Master the art of lead generation, qualification, and conversion with our comprehensive lead management system.',
            features: [
              { title: 'Lead Capture', description: 'Capture leads from multiple sources and channels', icon: FileText },
              { title: 'Pipeline Management', description: 'Track leads through your sales funnel', icon: TrendingUp },
              { title: 'Lead Scoring', description: 'Prioritize leads based on engagement and potential', icon: CheckSquare }
            ]
          };
        case 'clients':
          return {
            title: 'Client Relationship Management',
            description: 'Build lasting client relationships with comprehensive CRM tools designed for sales professionals.',
            features: [
              { title: 'Client Profiles', description: 'Maintain detailed client information and history', icon: Building2 },
              { title: 'Communication Tracking', description: 'Track all client interactions and touchpoints', icon: Calendar },
              { title: 'Relationship Mapping', description: 'Map client relationships and decision makers', icon: Users }
            ]
          };
        case 'activities':
          return {
            title: 'Sales Activities & Projects',
            description: 'Manage your sales activities, client projects, and follow-ups with precision and efficiency.',
            features: [
              { title: 'Activity Planning', description: 'Plan and schedule sales activities', icon: Calendar },
              { title: 'Project Tracking', description: 'Track client projects and deliverables', icon: CheckSquare },
              { title: 'Follow-up Management', description: 'Automate and track follow-up activities', icon: Zap }
            ]
          };
        case 'vendors':
          return {
            title: 'Vendor Management',
            description: 'Manage vendor relationships and coordinate with vendors to deliver exceptional results for your clients.',
            features: [
              { title: 'Vendor Selection', description: 'Choose the right vendors for client projects', icon: Users },
              { title: 'Project Coordination', description: 'Coordinate vendor work with client requirements', icon: Calendar },
              { title: 'Quality Assurance', description: 'Ensure vendor deliverables meet client expectations', icon: CheckSquare }
            ]
          };
        case 'invoicing':
          return {
            title: 'Sales Invoicing & Billing',
            description: 'Streamline your invoicing process and ensure timely payments from clients.',
            features: [
              { title: 'Invoice Generation', description: 'Create professional invoices for clients', icon: Receipt },
              { title: 'Payment Tracking', description: 'Track invoice status and payments', icon: CreditCard },
              { title: 'Revenue Recognition', description: 'Track revenue and commission calculations', icon: TrendingUp }
            ]
          };
        case 'mapped-activities':
          return {
            title: 'Mapped Activities',
            description: 'Track and manage mapped activities with comprehensive visibility into project progress, resource allocation, and performance metrics.',
            features: [
              { title: 'Activity Mapping', description: 'Map activities to specific projects, clients, and vendors with detailed tracking and status updates', icon: ListChecks },
              { title: 'Progress Tracking', description: 'Monitor activity progress, completion rates, and performance metrics with real-time updates', icon: BarChart3 },
              { title: 'Resource Allocation', description: 'Allocate resources, assign team members, and manage workload distribution across mapped activities', icon: Users }
            ]
          };
        case 'mapped-tasks':
          return {
            title: 'Mapped Tasks',
            description: 'Organize and track mapped tasks with detailed task management, assignment tracking, and completion monitoring.',
            features: [
              { title: 'Task Organization', description: 'Organize tasks by priority, category, and project with advanced filtering and sorting capabilities', icon: ClipboardList },
              { title: 'Task Assignment', description: 'Assign tasks to team members, set deadlines, and track completion status with notifications', icon: CheckSquare },
              { title: 'Progress Monitoring', description: 'Monitor task progress, identify bottlenecks, and generate performance reports for better management', icon: BarChart3 }
            ]
          };
        case 'payment-requests':
          return {
            title: 'Payment Tracking',
            description: 'Track client payments, manage outstanding amounts, and ensure cash flow.',
            features: [
              { title: 'Payment Status', description: 'Monitor payment status and due dates', icon: CreditCard },
              { title: 'Outstanding Tracking', description: 'Track outstanding payments and follow-ups', icon: FileText },
              { title: 'Cash Flow Management', description: 'Manage cash flow and payment schedules', icon: TrendingUp }
            ]
          };
        case 'todo':
          return {
            title: 'Sales Task Management',
            description: 'Organize your sales tasks, set priorities, and never miss important follow-ups.',
            features: [
              { title: 'Task Organization', description: 'Organize tasks by priority and deadline', icon: CheckSquare },
              { title: 'Follow-up Reminders', description: 'Set reminders for client follow-ups', icon: Calendar },
              { title: 'Activity Tracking', description: 'Track daily sales activities and goals', icon: BarChart3 }
            ]
          };
        case 'automations':
          return {
            title: 'Sales Automation',
            description: 'Automate repetitive sales tasks and focus on what matters most - closing deals.',
            features: [
              { title: 'Email Automation', description: 'Automate follow-up emails and sequences', icon: Zap },
              { title: 'Lead Nurturing', description: 'Automate lead nurturing campaigns', icon: FileText },
              { title: 'Task Automation', description: 'Automate routine sales tasks', icon: CheckSquare }
            ]
          };
        case 'hrms':
          return {
            title: 'Human Resource Management System',
            description: 'Comprehensive HR management with attendance tracking, performance management, and employee lifecycle management.',
            features: [
              { title: 'Attendance Tracking', description: 'Track employee attendance, working hours, and generate attendance reports with real-time monitoring', icon: UserCheck },
              { title: 'Performance Management', description: 'Set KRAs, track performance metrics, and conduct regular performance reviews with detailed analytics', icon: BarChart3 },
              { title: 'Employee Management', description: 'Manage employee profiles, roles, permissions, and organizational hierarchy with complete lifecycle tracking', icon: Users }
            ]
          };
      }
    }

    if (userFunction === 'Finance') {
      switch (sectionId) {
        case 'getting-started':
          return {
            title: 'Welcome to CupShup AI - Finance Team',
            description: 'CupShup AI is your comprehensive financial management platform designed to help you manage invoicing, billing, payments, and financial operations with precision and efficiency. This guide will help you master the tools and features specifically designed for finance professionals.',
            features: [
              { title: 'Financial Management', description: 'Comprehensive invoicing, billing, and payment tracking system', icon: Receipt },
              { title: 'Client Financial Relations', description: 'Manage client financial relationships and billing processes', icon: Building2 },
              { title: 'Financial Analytics', description: 'Track financial KPIs, revenue metrics, and payment analytics', icon: BarChart3 }
            ]
          };
        case 'vendors':
          return {
            title: 'Vendor Financial Management',
            description: 'Manage vendor financial relationships, payment processing, and financial coordination to ensure smooth business operations.',
            features: [
              { title: 'Vendor Payment Processing', description: 'Process vendor payments and manage payment schedules', icon: CreditCard },
              { title: 'Financial Coordination', description: 'Coordinate financial activities with vendors and track payment status', icon: Users },
              { title: 'Vendor Financial Analytics', description: 'Monitor vendor payment patterns and financial performance metrics', icon: BarChart3 }
            ]
          };
        case 'clients':
          return {
            title: 'Client Financial Management',
            description: 'Comprehensive client financial relationship management with advanced billing, invoicing, and payment tracking capabilities.',
            features: [
              { title: 'Client Billing Management', description: 'Manage client billing cycles, invoicing, and payment tracking', icon: Receipt },
              { title: 'Financial Relationship Tracking', description: 'Track client financial history, payment patterns, and credit management', icon: Building2 },
              { title: 'Revenue Analytics', description: 'Analyze client revenue, profitability, and financial performance metrics', icon: BarChart3 }
            ]
          };
        case 'activities':
          return {
            title: 'Financial Activities & Projects',
            description: 'Track financial activities, project costs, and revenue streams with comprehensive financial project management.',
            features: [
              { title: 'Project Cost Tracking', description: 'Track project costs, budgets, and financial performance', icon: Calculator },
              { title: 'Revenue Management', description: 'Manage revenue streams and track financial project outcomes', icon: TrendingUp },
              { title: 'Financial Activity Planning', description: 'Plan and schedule financial activities and budget cycles', icon: Calendar }
            ]
          };
        case 'mapped-activities':
          return {
            title: 'Mapped Financial Activities',
            description: 'Track and manage mapped financial activities with comprehensive visibility into financial progress, cost allocation, and revenue metrics.',
            features: [
              { title: 'Financial Activity Mapping', description: 'Map activities to specific financial processes, budgets, and revenue streams', icon: ListChecks },
              { title: 'Cost Allocation Tracking', description: 'Track cost allocation across activities and monitor financial performance', icon: BarChart3 },
              { title: 'Revenue Stream Management', description: 'Manage revenue streams and track financial activity outcomes', icon: TrendingUp }
            ]
          };
        case 'mapped-tasks':
          return {
            title: 'Mapped Financial Tasks',
            description: 'Organize and track mapped financial tasks with detailed task management, cost tracking, and financial completion monitoring.',
            features: [
              { title: 'Financial Task Organization', description: 'Organize tasks by financial priority, budget category, and revenue impact', icon: ClipboardList },
              { title: 'Cost Tracking', description: 'Track task costs, budget allocation, and financial task completion', icon: Calculator },
              { title: 'Financial Progress Monitoring', description: 'Monitor financial task progress and generate financial performance reports', icon: BarChart3 }
            ]
          };
        case 'todo':
          return {
            title: 'Financial Task Management',
            description: 'Organize your financial tasks, set priorities, and manage daily financial workflows efficiently.',
            features: [
              { title: 'Financial Task Organization', description: 'Organize tasks by financial priority, budget category, and deadline', icon: CheckSquare },
              { title: 'Budget Management', description: 'Manage budget-related tasks and financial planning activities', icon: Calculator },
              { title: 'Financial Performance Tracking', description: 'Track daily financial activities and performance metrics', icon: BarChart3 }
            ]
          };
        case 'invoicing':
          return {
            title: 'Invoicing & Billing Management',
            description: 'Comprehensive invoicing and billing system with advanced features for client billing, payment tracking, and financial management.',
            features: [
              { title: 'Invoice Generation', description: 'Generate professional invoices with automated billing cycles and customizable templates', icon: Receipt },
              { title: 'Payment Tracking', description: 'Track invoice payments, outstanding amounts, and payment history with real-time updates', icon: CreditCard },
              { title: 'Financial Reporting', description: 'Generate detailed financial reports, revenue analytics, and billing performance metrics', icon: BarChart3 }
            ]
          };
        case 'payment-requests':
          return {
            title: 'Payment Request Management',
            description: 'Manage payment requests and approvals with streamlined workflows, approval chains, and financial tracking.',
            features: [
              { title: 'Payment Request Processing', description: 'Process payment requests with detailed financial documentation and approval workflows', icon: CreditCard },
              { title: 'Approval Management', description: 'Configure approval chains and automate approval processes for financial efficiency', icon: CheckSquare },
              { title: 'Financial Tracking', description: 'Track payment history, spending patterns, and financial request analytics', icon: BarChart3 }
            ]
          };
        case 'hrms':
          return {
            title: 'Human Resource Management System',
            description: 'Comprehensive HR management with attendance tracking, performance management, and employee lifecycle management for finance teams.',
            features: [
              { title: 'Financial HR Analytics', description: 'Track employee costs, payroll analytics, and HR financial metrics', icon: UserCheck },
              { title: 'Performance Management', description: 'Set KRAs, track performance metrics, and conduct financial performance reviews', icon: BarChart3 },
              { title: 'Team Financial Management', description: 'Manage team financial profiles, roles, and financial responsibilities', icon: Users }
            ]
          };
      }
    }

    if (userFunction === 'Marketing Intern') {
      switch (sectionId) {
        case 'getting-started':
          return {
            title: 'Welcome to CupShup AI - Marketing Intern',
            description: 'CupShup AI is your comprehensive marketing platform designed to help you learn, grow, and contribute to marketing campaigns, lead generation, and client management. This guide will help you master the tools and features specifically designed for marketing interns and entry-level marketing professionals.',
            features: [
              { title: 'Marketing Campaign Management', description: 'Learn to manage marketing campaigns and track performance metrics', icon: Calendar },
              { title: 'Lead Generation & Management', description: 'Master lead generation techniques and pipeline management', icon: FileText },
              { title: 'Client Marketing Relations', description: 'Build and maintain client marketing relationships', icon: Building2 }
            ]
          };
        case 'vendors':
          return {
            title: 'Vendor Marketing Management',
            description: 'Learn to manage vendor relationships and coordinate marketing activities with vendors to support campaign execution.',
            features: [
              { title: 'Vendor Marketing Coordination', description: 'Coordinate with vendors for marketing activities and campaign deliverables', icon: Users },
              { title: 'Marketing Performance Tracking', description: 'Track vendor marketing performance and campaign metrics', icon: BarChart3 },
              { title: 'Campaign Collaboration', description: 'Collaborate with vendors on marketing campaigns and creative projects', icon: Calendar }
            ]
          };
        case 'clients':
          return {
            title: 'Client Marketing Management',
            description: 'Build and maintain client marketing relationships with comprehensive tools for campaign management and client communication.',
            features: [
              { title: 'Client Marketing Profiles', description: 'Maintain detailed client marketing information and campaign history', icon: Building2 },
              { title: 'Campaign Communication', description: 'Track client marketing communications and campaign updates', icon: Calendar },
              { title: 'Marketing Relationship Building', description: 'Build strong client marketing relationships and understand client needs', icon: Users }
            ]
          };
        case 'activities':
          return {
            title: 'Marketing Activities & Campaigns',
            description: 'Manage marketing activities, campaigns, and projects with comprehensive tools for campaign planning and execution.',
            features: [
              { title: 'Campaign Planning', description: 'Plan and schedule marketing campaigns and activities', icon: Calendar },
              { title: 'Marketing Project Tracking', description: 'Track marketing projects and campaign deliverables', icon: CheckSquare },
              { title: 'Activity Management', description: 'Manage daily marketing activities and campaign tasks', icon: BarChart3 }
            ]
          };
        case 'mapped-activities':
          return {
            title: 'Mapped Marketing Activities',
            description: 'Track and manage mapped marketing activities with comprehensive visibility into campaign progress, resource allocation, and performance metrics.',
            features: [
              { title: 'Marketing Activity Mapping', description: 'Map activities to specific marketing campaigns, channels, and objectives', icon: ListChecks },
              { title: 'Campaign Progress Tracking', description: 'Monitor campaign progress, completion rates, and marketing performance metrics', icon: BarChart3 },
              { title: 'Resource Allocation', description: 'Allocate marketing resources and track campaign resource utilization', icon: Users }
            ]
          };
        case 'mapped-tasks':
          return {
            title: 'Mapped Marketing Tasks',
            description: 'Organize and track mapped marketing tasks with detailed task management, campaign tracking, and completion monitoring.',
            features: [
              { title: 'Marketing Task Organization', description: 'Organize tasks by campaign priority, marketing channel, and deadline', icon: ClipboardList },
              { title: 'Campaign Task Assignment', description: 'Assign marketing tasks to team members and track campaign progress', icon: CheckSquare },
              { title: 'Marketing Progress Monitoring', description: 'Monitor marketing task progress and generate campaign performance reports', icon: BarChart3 }
            ]
          };
        case 'todo':
          return {
            title: 'Marketing Task Management',
            description: 'Organize your marketing tasks, set priorities, and manage daily marketing workflows efficiently.',
            features: [
              { title: 'Marketing Task Organization', description: 'Organize tasks by campaign priority, marketing channel, and deadline', icon: CheckSquare },
              { title: 'Campaign Management', description: 'Manage marketing campaign tasks and creative workflows', icon: Calendar },
              { title: 'Marketing Performance Tracking', description: 'Track daily marketing activities and campaign performance metrics', icon: BarChart3 }
            ]
          };
        case 'payment-requests':
          return {
            title: 'Payment Request Management',
            description: 'Learn to manage payment requests and approvals with streamlined workflows for marketing expenses and campaign budgets.',
            features: [
              { title: 'Marketing Expense Management', description: 'Create and submit payment requests for marketing expenses and campaign costs', icon: CreditCard },
              { title: 'Budget Tracking', description: 'Track marketing budgets and campaign spending patterns', icon: Calculator },
              { title: 'Approval Workflows', description: 'Learn approval processes for marketing expenses and campaign budgets', icon: CheckSquare }
            ]
          };
        case 'leads':
          return {
            title: 'Lead Management & Pipeline',
            description: 'Master the art of lead generation, qualification, and conversion with our comprehensive lead management system designed for marketing professionals.',
            features: [
              { title: 'Lead Generation', description: 'Learn to generate leads from multiple marketing channels and sources', icon: FileText },
              { title: 'Lead Qualification', description: 'Qualify leads and track them through the marketing funnel', icon: CheckSquare },
              { title: 'Pipeline Management', description: 'Manage lead pipeline and track conversion rates', icon: TrendingUp }
            ]
          };
        case 'hrms':
          return {
            title: 'Human Resource Management System',
            description: 'Comprehensive HR management with attendance tracking, performance management, and employee lifecycle management for marketing interns.',
            features: [
              { title: 'Attendance Tracking', description: 'Track your attendance, working hours, and generate attendance reports', icon: UserCheck },
              { title: 'Learning & Development', description: 'Track learning progress, skill development, and performance metrics', icon: BarChart3 },
              { title: 'Team Collaboration', description: 'Collaborate with marketing team members and learn from experienced professionals', icon: Users }
            ]
          };
      }
    }

    if (userFunction === 'Vendor' || userFunction === 'vendor') {
      switch (sectionId) {
        case 'getting-started':
          return {
            title: 'Welcome to CupShup AI - Vendor Portal',
            description: 'Welcome to the CupShup AI Vendor Portal! This platform connects you with exciting projects and opportunities. Complete your profile setup to unlock all features and start receiving project assignments.',
            features: [
              { title: 'Profile Setup', description: 'Complete your vendor profile with business details, services, and portfolio to get matched with relevant projects', icon: User },
              { title: 'Project Discovery', description: 'Browse available activities and projects that match your skills and expertise', icon: Calendar },
              { title: 'Task Management', description: 'Manage your assigned tasks and track project progress efficiently', icon: CheckSquare }
            ]
          };
        case 'dashboard':
          return {
            title: 'Vendor Dashboard',
            description: 'Your central command center where you can monitor your profile completion, view available opportunities, and track your current project status.',
            features: [
              { title: 'Profile Completion', description: 'Track your profile setup progress and see what information is still needed to unlock all features', icon: BarChart3 },
              { title: 'Available Opportunities', description: 'View new activities and projects that match your profile and skills', icon: Calendar },
              { title: 'Current Projects', description: 'Monitor your active projects, upcoming deadlines, and task completion status', icon: CheckSquare }
            ]
          };
        case 'activities':
          return {
            title: 'Available Activities',
            description: 'Browse all available activities and projects that you can participate in. Filter by your skills, location, and preferences to find the perfect match.',
            features: [
              { title: 'Activity Browser', description: 'Explore available activities across different categories like Manpower, Printing, Design, and more', icon: Calendar },
              { title: 'Project Details', description: 'View detailed project requirements, timelines, and compensation information', icon: FileText },
              { title: 'Apply & Express Interest', description: 'Apply for activities that interest you and express your availability for future projects', icon: CheckSquare }
            ]
          };
        case 'my-activities':
          return {
            title: 'My Activities',
            description: 'Manage all your assigned activities and projects. Track progress, communicate with clients, and ensure timely delivery of your work.',
            features: [
              { title: 'Active Projects', description: 'View and manage all your currently assigned activities and their progress status', icon: CheckSquare },
              { title: 'Client Communication', description: 'Communicate directly with clients and project managers through the platform', icon: Users },
              { title: 'Deliverable Management', description: 'Track deliverables, upload completed work, and manage project milestones', icon: Calendar }
            ]
          };
        case 'my-tasks':
          return {
            title: 'My Tasks',
            description: 'Organize and track all your assigned tasks. Set priorities, update progress, and ensure nothing falls through the cracks.',
            features: [
              { title: 'Task Organization', description: 'Organize tasks by project, priority, and deadline for efficient workflow management', icon: ClipboardList },
              { title: 'Progress Updates', description: 'Update task completion status and provide progress reports to clients', icon: CheckSquare },
              { title: 'Deadline Management', description: 'Track upcoming deadlines and receive notifications for urgent tasks', icon: Calendar }
            ]
          };
        case 'profile':
          return {
            title: 'Vendor Profile Management',
            description: 'Complete and maintain your vendor profile to maximize your chances of getting matched with relevant projects. Showcase your skills, portfolio, and professional credentials.',
            features: [
              { title: 'Business Information', description: 'Complete your company details, business registration, and contact information', icon: User },
              { title: 'Service Categories', description: 'Select your service categories (Manpower, Printing, Design, etc.) and define your capabilities', icon: CheckSquare },
              { title: 'Portfolio & Credentials', description: 'Upload your work samples, client testimonials, and professional certifications', icon: FileText }
            ]
          };
      }
    }

    if (userFunction === 'Client' || userFunction === 'client') {
      switch (sectionId) {
        case 'getting-started':
          return {
            title: 'Welcome to CupShup AI - Client Portal',
            description: 'Welcome to the CupShup AI Client Portal! This comprehensive platform empowers you to plan, execute, and manage your marketing campaigns with advanced AI tools and seamless project management. This guide will help you master all the features designed specifically for clients.',
            features: [
              { title: 'Media Planning', description: 'Plan and strategize your media campaigns with advanced planning tools', icon: Target },
              { title: 'AI-Powered Tools', description: 'Access cutting-edge AI tools and agents for marketing automation', icon: Cpu },
              { title: 'Campaign Management', description: 'Create and manage email and WhatsApp campaigns with ease', icon: Mail }
            ]
          };
        case 'dashboard':
          return {
            title: 'Client Dashboard',
            description: 'Your central command center where you can monitor all your marketing activities, track campaign performance, and manage your projects in one place.',
            features: [
              { title: 'Project Overview', description: 'View all your active projects, campaigns, and their current status', icon: BarChart3 },
              { title: 'Performance Metrics', description: 'Track campaign performance, engagement rates, and ROI metrics', icon: TrendingUp },
              { title: 'Quick Actions', description: 'Access frequently used features and create new campaigns quickly', icon: CheckSquare }
            ]
          };
        case 'media-planning':
          return {
            title: 'Media Planning',
            description: 'Plan and strategize your media campaigns with comprehensive planning tools. Define your target audience, set campaign objectives, and create detailed media strategies.',
            features: [
              { title: 'Campaign Strategy', description: 'Define campaign objectives, target audience, and media mix strategies', icon: Target },
              { title: 'Budget Planning', description: 'Plan and allocate budgets across different media channels and campaigns', icon: Calculator },
              { title: 'Timeline Management', description: 'Create detailed campaign timelines and milestone tracking', icon: Calendar }
            ]
          };
        case 'my-plans':
          return {
            title: 'My Plans',
            description: 'Manage all your marketing plans and strategies. View, edit, and track the progress of your planned campaigns and marketing initiatives.',
            features: [
              { title: 'Plan Management', description: 'Create, edit, and manage your marketing plans and strategies', icon: ClipboardList },
              { title: 'Plan Templates', description: 'Use pre-built templates and create custom plan templates', icon: FileText },
              { title: 'Plan Collaboration', description: 'Collaborate with your team and CupShup experts on plan development', icon: Users }
            ]
          };
        case 'activities':
          return {
            title: 'All Activities',
            description: 'View and track all your project activities across different campaigns and initiatives. Monitor progress, deadlines, and deliverables.',
            features: [
              { title: 'Activity Overview', description: 'View all activities across your projects with detailed status tracking', icon: ListChecks },
              { title: 'Progress Monitoring', description: 'Track activity progress, completion rates, and milestone achievements', icon: BarChart3 },
              { title: 'Activity Filtering', description: 'Filter activities by project, status, priority, and team member', icon: CheckSquare }
            ]
          };
        case 'my-tasks':
          return {
            title: 'All Tasks',
            description: 'Monitor all tasks across your projects and campaigns. Track task assignments, deadlines, and completion status.',
            features: [
              { title: 'Task Overview', description: 'View all tasks across your projects with comprehensive status tracking', icon: FileText },
              { title: 'Task Management', description: 'Assign tasks, set priorities, and track completion progress', icon: CheckSquare },
              { title: 'Deadline Tracking', description: 'Monitor upcoming deadlines and receive notifications for urgent tasks', icon: Calendar }
            ]
          };
        case 'ai-tools':
          return {
            title: 'AI Tools',
            description: 'Access powerful AI-powered marketing tools that help you create content, analyze data, and optimize your campaigns automatically.',
            features: [
              { title: 'Content Generation', description: 'Generate marketing content, copy, and creative assets using AI', icon: Cpu },
              { title: 'Data Analytics', description: 'Analyze campaign data and get AI-powered insights and recommendations', icon: BarChart3 },
              { title: 'Automation Tools', description: 'Automate repetitive tasks and optimize campaign performance', icon: Zap }
            ]
          };
        case 'ai-agents':
          return {
            title: 'AI Agents',
            description: 'Interact with specialized AI agents that provide personalized marketing assistance, campaign optimization, and strategic guidance.',
            features: [
              { title: 'Marketing Assistant', description: 'Get personalized marketing advice and campaign optimization suggestions', icon: Bot },
              { title: 'Content Advisor', description: 'Receive AI-powered content recommendations and creative guidance', icon: FileText },
              { title: 'Performance Optimizer', description: 'Get automated suggestions to improve campaign performance and ROI', icon: TrendingUp }
            ]
          };
        case 'email-campaign':
          return {
            title: 'Email Campaign',
            description: 'Create, launch, and manage email marketing campaigns with advanced features for audience segmentation, personalization, and performance tracking.',
            features: [
              { title: 'Campaign Creation', description: 'Design and create professional email campaigns with drag-and-drop editor', icon: Mail },
              { title: 'Audience Segmentation', description: 'Segment your audience and personalize campaigns for better engagement', icon: Users },
              { title: 'Performance Analytics', description: 'Track email performance, open rates, click rates, and conversion metrics', icon: BarChart3 }
            ]
          };
        case 'whatsapp-campaign':
          return {
            title: 'WhatsApp Campaign',
            description: 'Launch and manage WhatsApp marketing campaigns with features for broadcast messaging, chatbot integration, and engagement tracking.',
            features: [
              { title: 'Broadcast Messaging', description: 'Send targeted broadcast messages to your WhatsApp contact lists', icon: MessageSquare },
              { title: 'Chatbot Integration', description: 'Integrate AI chatbots for automated customer interactions', icon: Bot },
              { title: 'Engagement Tracking', description: 'Track message delivery, read receipts, and customer engagement', icon: BarChart3 }
            ]
          };
      }
    }

    // Default content for other roles
    return null;
  };

  // Render role-based content for any section
  const renderRoleBasedSection = (sectionId: string) => {
    const content = getRoleBasedContent(sectionId);
    if (!content) return null;

    return (
      <div className="space-y-8">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
            {content.title}
          </h3>
          <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
            {content.description}
          </p>
        </div>

        <div className="bg-gradient-to-r from-[#00a979]/10 to-[#0da5e1]/10 p-4 sm:p-6 lg:p-8 rounded-2xl">
          <h4 className="text-xl sm:text-2xl font-montserrat font-semibold text-[#1d2121] mb-4 sm:mb-6 text-center">Key Features</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {content.features.map((feature, index) => {
              const colors = ['#00a979', '#0da5e1'];
              const color = colors[index % colors.length];
              const IconComponent = feature.icon;

              return (
                <div key={index} className="bg-white/90 p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className={`bg-gradient-to-br from-[${color}]/15 to-[${color}]/8 p-2 sm:p-3 rounded-lg w-fit mb-3 sm:mb-4`}>
                    <IconComponent className={`h-5 w-5 sm:h-6 sm:w-6 text-[${color}]`} />
                  </div>
                  <h5 className={`font-montserrat font-semibold text-[${color}] mb-2 sm:mb-3 text-base sm:text-lg`}>{feature.title}</h5>
                  <p className="font-roboto text-xs sm:text-sm text-gray-700 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    // Check if we have role-based content for this section
    const roleBasedContent = renderRoleBasedSection(selectedSection);
    if (roleBasedContent) return roleBasedContent;

    // Fall back to default content
    switch (selectedSection) {
      case 'getting-started': {
        const gettingStartedContent = getRoleBasedContent('getting-started');
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                {gettingStartedContent?.title || 'Welcome to CupShup AI 3.0'}
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                {gettingStartedContent?.description || 'CupShup AI is your comprehensive marketing agency management platform that transforms how you manage projects, teams, and client relationships. Built with cutting-edge technology and designed for creative excellence, CupShup AI embodies our philosophy of "tech as brain and creative at heart."'}
              </p>
            </div>

            <div className="bg-gradient-to-r from-[#00a979]/10 to-[#0da5e1]/10 p-4 sm:p-6 lg:p-8 rounded-2xl">
              <h4 className="text-xl sm:text-2xl font-montserrat font-semibold text-[#1d2121] mb-4 sm:mb-6 text-center">Platform Overview</h4>
              <p className="font-roboto text-[#1d2121] mb-4 sm:mb-6 text-center max-w-2xl mx-auto text-sm sm:text-base">
                The platform is organized into main sections accessible via the left sidebar. Each section contains specific
                tools and features designed for different aspects of your business operations.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="bg-white/90 p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-2 sm:p-3 rounded-lg w-fit mb-3 sm:mb-4">
                    <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-2 sm:mb-3 text-base sm:text-lg">Dashboard</h5>
                  <p className="font-roboto text-xs sm:text-sm text-gray-700 leading-relaxed">Real-time analytics, business insights, and comprehensive metrics tracking</p>
                </div>
                <div className="bg-white/90 p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-2 sm:p-3 rounded-lg w-fit mb-3 sm:mb-4">
                    <Users className="h-5 w-5 sm:h-6 sm:w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-2 sm:mb-3 text-base sm:text-lg">Vendors</h5>
                  <p className="font-roboto text-xs sm:text-sm text-gray-700 leading-relaxed">Partner management, onboarding, and performance tracking</p>
                </div>
                <div className="bg-white/90 p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-2 sm:p-3 rounded-lg w-fit mb-3 sm:mb-4">
                    <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-2 sm:mb-3 text-base sm:text-lg">Clients</h5>
                  <p className="font-roboto text-xs sm:text-sm text-gray-700 leading-relaxed">Customer relationships, brand management, and media planning</p>
                </div>
                <div className="bg-white/90 p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-2 sm:p-3 rounded-lg w-fit mb-3 sm:mb-4">
                    <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-2 sm:mb-3 text-base sm:text-lg">Activities</h5>
                  <p className="font-roboto text-xs sm:text-sm text-gray-700 leading-relaxed">Project management, campaign execution, and task tracking</p>
                </div>
                <div className="bg-white/90 p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-2 sm:p-3 rounded-lg w-fit mb-3 sm:mb-4">
                    <UserCheck className="h-5 w-5 sm:h-6 sm:w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-2 sm:mb-3 text-base sm:text-lg">HRMS</h5>
                  <p className="font-roboto text-xs sm:text-sm text-gray-700 leading-relaxed">Human resource management, attendance tracking, and KRA system</p>
                </div>
                <div className="bg-white/90 p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-2 sm:p-3 rounded-lg w-fit mb-3 sm:mb-4">
                    <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-2 sm:mb-3 text-base sm:text-lg">Automations</h5>
                  <p className="font-roboto text-xs sm:text-sm text-gray-700 leading-relaxed">AI-powered tools for content creation, SEO analysis, and lead generation</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 lg:p-8 shadow-sm">
              <h4 className="text-xl sm:text-2xl font-montserrat font-semibold text-[#1d2121] mb-4 sm:mb-6 text-center">Daily Workflow Checklist</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-[#00a979] text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">1</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-base sm:text-lg text-[#1d2121] mb-1">Dashboard Review</h5>
                      <p className="font-roboto text-gray-700 text-sm sm:text-base">Check overall metrics, revenue targets, and business health indicators</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-[#0da5e1] text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">2</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-base sm:text-lg text-[#1d2121] mb-1">Payment Approvals</h5>
                      <p className="font-roboto text-gray-700 text-sm sm:text-base">Review and approve pending payment requests from team members</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-[#00a979] text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">3</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-base sm:text-lg text-[#1d2121] mb-1">Team Management</h5>
                      <p className="font-roboto text-gray-700 text-sm sm:text-base">Monitor attendance, KRA progress, and team performance metrics</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-[#0da5e1] text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">4</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-base sm:text-lg text-[#1d2121] mb-1">Project Monitoring</h5>
                      <p className="font-roboto text-gray-700 text-sm sm:text-base">Track active projects, deadlines, and vendor performance</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-[#00a979] text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">5</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-base sm:text-lg text-[#1d2121] mb-1">Lead Management</h5>
                      <p className="font-roboto text-gray-700 text-sm sm:text-base">Review sales pipeline, follow up on leads, and update CRM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-[#0da5e1] text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">6</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-base sm:text-lg text-[#1d2121] mb-1">Strategic Planning</h5>
                      <p className="font-roboto text-gray-700 text-sm sm:text-base">Use automation tools and analytics for business growth</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'dashboard':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Dashboard & Analytics Hub
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                The Dashboard is your command center, providing real-time insights into all business operations.
                It's designed to give you an at-a-glance view of key metrics, pending tasks, and overall business health.
              </p>
            </div>

            <div className="bg-gradient-to-r from-[#00a979]/10 to-[#0da5e1]/10 p-8 rounded-2xl">
              <h4 className="text-2xl font-montserrat font-semibold text-[#1d2121] mb-6 text-center">Key Dashboard Components</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <BarChart3 className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Overall Metrics</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">High-level KPIs including total activities, tasks, vendors, and revenue tracking</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <Zap className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Revenue Analytics</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Monthly revenue tracking, targets, and period-over-period comparisons</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <Calendar className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Task Management</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Task completion rates, pending items, and productivity insights</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <Users className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Activity Tracking</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Project status, city distribution, and completion trends</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <UserCheck className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Team Performance</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Attendance tracking, KRA progress, and team productivity metrics</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <Building2 className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Client Analytics</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Client engagement, brand performance, and relationship metrics</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <h4 className="text-2xl font-montserrat font-semibold text-[#1d2121] mb-6 text-center">Daily Dashboard Workflow</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#00a979] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Morning Review</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Start your day by reviewing overall metrics, revenue targets, and business health indicators</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-[#0da5e1] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Trend Analysis</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Use period-over-period comparisons to identify growth patterns and performance trends</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-[#00a979] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Issue Identification</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Look for red flags in task completion, budget overruns, or team performance</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#0da5e1] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Drill Down Analysis</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Click on metrics to access detailed views and comprehensive analytics</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-[#00a979] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">5</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Strategic Planning</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Use insights to make data-driven decisions and plan future strategies</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-[#0da5e1] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">6</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Team Communication</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Share insights with team members and coordinate action plans</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#0da5e1]/10 to-[#00a979]/10 p-8 rounded-2xl">
              <h4 className="text-2xl font-montserrat font-semibold text-[#1d2121] mb-6 text-center">Advanced Dashboard Features</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/90 p-6 rounded-xl shadow-sm">
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Real-time Notifications</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Get instant alerts for payment approvals, task completions, and important updates</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm">
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Customizable Views</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Filter and customize dashboard views based on your role and preferences</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm">
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Export Capabilities</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Export reports and analytics for presentations and external sharing</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm">
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Mobile Optimization</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Access dashboard insights on-the-go with mobile-optimized interface</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'vendors':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Vendor Management System
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                The Vendor Management System allows you to oversee all external partners, from initial onboarding
                through ongoing performance monitoring. This is where you manage your service provider ecosystem.
              </p>
            </div>

            <div className="bg-gradient-to-r from-[#00a979]/10 to-[#0da5e1]/10 p-8 rounded-2xl">
              <h4 className="text-2xl font-montserrat font-semibold text-[#1d2121] mb-6 text-center">Key Vendor Features</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <Users className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Vendor Directory</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Complete list of all registered vendors with profile information and performance ratings</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <Calendar className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Onboarding Status</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Track 4-step onboarding progress with completion percentages and status indicators</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <Zap className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Service Categories</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Manpower, Printing, Logistics, Goodies, Engagement, Alliance, Production</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <BarChart3 className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Performance Tracking</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Monitor vendor performance, ratings, and project completion metrics</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <Building2 className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Banking Details</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Secure storage of payment information and financial details for seamless transactions</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <UserCheck className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Portfolio Management</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Upload and manage vendor portfolios, certifications, and work samples</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <h4 className="text-2xl font-montserrat font-semibold text-[#1d2121] mb-6 text-center">4-Step Vendor Onboarding Process</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="border-l-4 border-[#00a979] pl-6 py-4 bg-gradient-to-r from-[#00a979]/5 to-transparent rounded-r-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-[#00a979] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</div>
                      <h5 className="font-montserrat font-semibold text-[#00a979] text-lg">Basic Profile (25%)</h5>
                    </div>
                    <ul className="font-roboto text-gray-700 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-[#00a979] mt-1">•</span>
                        <span>Company name and business registration details</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#00a979] mt-1">•</span>
                        <span>Primary contact person and role information</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#00a979] mt-1">•</span>
                        <span>Business address and contact numbers</span>
                      </li>
                    </ul>
                  </div>
                  <div className="border-l-4 border-[#0da5e1] pl-6 py-4 bg-gradient-to-r from-[#0da5e1]/5 to-transparent rounded-r-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-[#0da5e1] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</div>
                      <h5 className="font-montserrat font-semibold text-[#0da5e1] text-lg">Service Selection (50%)</h5>
                    </div>
                    <ul className="font-roboto text-gray-700 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-[#0da5e1] mt-1">•</span>
                        <span>Choose service categories (Manpower, Printing, etc.)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#0da5e1] mt-1">•</span>
                        <span>Define capabilities and specializations</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#0da5e1] mt-1">•</span>
                        <span>Set service area and coverage zones</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="border-l-4 border-[#00a979] pl-6 py-4 bg-gradient-to-r from-[#00a979]/5 to-transparent rounded-r-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-[#00a979] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</div>
                      <h5 className="font-montserrat font-semibold text-[#00a979] text-lg">Banking Details (75%)</h5>
                    </div>
                    <ul className="font-roboto text-gray-700 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-[#00a979] mt-1">•</span>
                        <span>Bank account and IFSC code details</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#00a979] mt-1">•</span>
                        <span>UPI ID for quick payment processing</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#00a979] mt-1">•</span>
                        <span>GST registration (if applicable)</span>
                      </li>
                    </ul>
                  </div>
                  <div className="border-l-4 border-[#0da5e1] pl-6 py-4 bg-gradient-to-r from-[#0da5e1]/5 to-transparent rounded-r-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-[#0da5e1] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</div>
                      <h5 className="font-montserrat font-semibold text-[#0da5e1] text-lg">Portfolio Upload (100%)</h5>
                    </div>
                    <ul className="font-roboto text-gray-700 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-[#0da5e1] mt-1">•</span>
                        <span>Previous work samples and case studies</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#0da5e1] mt-1">•</span>
                        <span>Client testimonials and references</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#0da5e1] mt-1">•</span>
                        <span>Certifications and professional credentials</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#0da5e1]/10 to-[#00a979]/10 p-8 rounded-2xl">
              <h4 className="text-2xl font-montserrat font-semibold text-[#1d2121] mb-6 text-center">Vendor Management Best Practices</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/90 p-6 rounded-xl shadow-sm">
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Regular Performance Reviews</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Conduct monthly performance reviews and provide feedback to maintain quality standards</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm">
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Clear Communication</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Maintain clear communication channels and set expectations for project deliverables</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm">
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Payment Processing</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Ensure timely payment processing to maintain good vendor relationships</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm">
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Capacity Planning</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Monitor vendor capacity and availability for effective project planning</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'clients':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Client Relationship Management
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Comprehensive client management system that enables you to build strong relationships,
                manage brand activities, and provide exceptional service delivery.
              </p>
            </div>

            <div className="bg-gradient-to-r from-[#00a979]/10 to-[#0da5e1]/10 p-8 rounded-2xl">
              <h4 className="text-2xl font-montserrat font-semibold text-[#1d2121] mb-6 text-center">Client Management Features</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <Building2 className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Brand Management</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Manage multiple client brands with dedicated portals and customized experiences</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <Calendar className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Activity Tracking</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Monitor all client activities, project progress, and milestone achievements</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <Users className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Vendor Coordination</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Coordinate vendor activities and ensure seamless project execution</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <BarChart3 className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Performance Analytics</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Track client satisfaction, project success rates, and engagement metrics</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <Zap className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Media Planning</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Self-service media planning platform with RWA and corporate options</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <UserCheck className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Client Portal</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Dedicated client portal with real-time project visibility and communication</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <h4 className="text-2xl font-montserrat font-semibold text-[#1d2121] mb-6 text-center">Client Onboarding Process</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#00a979] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Brand Setup</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Create brand profiles with specific requirements and preferences</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-[#0da5e1] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Portal Access</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Provide branded client portal access with customized experience</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-[#00a979] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Project Initiation</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Set up initial activities and assign appropriate vendors</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#0da5e1] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Communication Setup</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Establish communication channels and notification preferences</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-[#00a979] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">5</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Media Planning</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Enable media planning tools and campaign management capabilities</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-[#0da5e1] text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0">6</div>
                    <div>
                      <h5 className="font-montserrat font-semibold text-xl text-[#1d2121] mb-2">Ongoing Support</h5>
                      <p className="font-roboto text-gray-700 leading-relaxed">Provide continuous support and relationship management</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'activities':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Activity & Project Management
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Complete project lifecycle management system that enables you to plan, execute,
                and monitor marketing campaigns from conception to completion.
              </p>
            </div>

            <div className="bg-gradient-to-r from-[#00a979]/10 to-[#0da5e1]/10 p-8 rounded-2xl">
              <h4 className="text-2xl font-montserrat font-semibold text-[#1d2121] mb-6 text-center">Activity Management Features</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <Calendar className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Project Planning</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Create and manage marketing activities with detailed timelines and budgets</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <Users className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Vendor Assignment</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Assign vendors to specific activities with clear instructions and deliverables</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <BarChart3 className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Progress Tracking</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Monitor real-time progress and completion status across all activities</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <Zap className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Task Management</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Create and track individual tasks within activities for detailed execution</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <Building2 className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Revenue Tracking</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Track revenue generation and profitability for each activity</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <UserCheck className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Quality Control</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Implement quality checks and approval workflows for deliverables</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'operations':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Operations Management
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Streamline operational workflows, manage processes, and optimize business operations with comprehensive tools and analytics.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50/50 p-8 rounded-2xl border border-gray-200/50 shadow-sm">
              <h4 className="text-2xl font-montserrat font-bold text-[#1d2121] mb-6 flex items-center gap-3">
                <Calculator className="h-6 w-6 text-[#0da5e1]" />
                Operations Features
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <Calculator className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Process Management</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Design, implement, and monitor operational processes with workflow automation and performance tracking.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <BarChart3 className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Operational Analytics</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Track operational KPIs, efficiency metrics, and performance indicators with real-time dashboards.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <Zap className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Workflow Optimization</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Optimize workflows, reduce bottlenecks, and improve operational efficiency with data-driven insights.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'mapped-activities':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Mapped Activities
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Track and manage mapped activities with comprehensive visibility into project progress, resource allocation, and performance metrics.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50/50 p-8 rounded-2xl border border-gray-200/50 shadow-sm">
              <h4 className="text-2xl font-montserrat font-bold text-[#1d2121] mb-6 flex items-center gap-3">
                <ListChecks className="h-6 w-6 text-[#0da5e1]" />
                Mapped Activities Features
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <ListChecks className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Activity Mapping</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Map activities to specific projects, clients, and vendors with detailed tracking and status updates.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <BarChart3 className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Progress Tracking</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Monitor activity progress, completion rates, and performance metrics with real-time updates.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <Users className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Resource Allocation</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Allocate resources, assign team members, and manage workload distribution across mapped activities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'mapped-tasks':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Mapped Tasks
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Organize and track mapped tasks with detailed task management, assignment tracking, and completion monitoring.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50/50 p-8 rounded-2xl border border-gray-200/50 shadow-sm">
              <h4 className="text-2xl font-montserrat font-bold text-[#1d2121] mb-6 flex items-center gap-3">
                <ClipboardList className="h-6 w-6 text-[#0da5e1]" />
                Mapped Tasks Features
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <ClipboardList className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Task Organization</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Organize tasks by priority, category, and project with advanced filtering and sorting capabilities.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <CheckSquare className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Task Assignment</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Assign tasks to team members, set deadlines, and track completion status with notifications.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <BarChart3 className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Progress Monitoring</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Monitor task progress, identify bottlenecks, and generate performance reports for better management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'todo':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Todo Management
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Personal and team task management with priority tracking, deadline management, and collaborative task organization.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50/50 p-8 rounded-2xl border border-gray-200/50 shadow-sm">
              <h4 className="text-2xl font-montserrat font-bold text-[#1d2121] mb-6 flex items-center gap-3">
                <CheckSquare className="h-6 w-6 text-[#0da5e1]" />
                Todo Features
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <CheckSquare className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Personal Tasks</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Create, organize, and track personal tasks with priority levels, due dates, and completion tracking.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <Users className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Team Collaboration</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Share tasks with team members, assign responsibilities, and collaborate on project deliverables.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <Calendar className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Deadline Management</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Set deadlines, receive reminders, and track overdue tasks with automated notifications.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'automations':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Automation Hub
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Automate workflows and processes to increase efficiency, reduce manual work, and streamline business operations.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50/50 p-8 rounded-2xl border border-gray-200/50 shadow-sm">
              <h4 className="text-2xl font-montserrat font-bold text-[#1d2121] mb-6 flex items-center gap-3">
                <Zap className="h-6 w-6 text-[#0da5e1]" />
                Automation Features
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <Zap className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Workflow Automation</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Create automated workflows for repetitive tasks, approvals, and notifications to save time and reduce errors.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <Calendar className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Scheduled Tasks</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Schedule automated tasks, reports, and notifications to run at specific times or intervals.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <BarChart3 className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Performance Analytics</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Monitor automation performance, track efficiency gains, and optimize workflows based on data insights.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'invoicing':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Invoicing System
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Complete billing and invoicing management with automated invoice generation, payment tracking, and financial reporting.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50/50 p-8 rounded-2xl border border-gray-200/50 shadow-sm">
              <h4 className="text-2xl font-montserrat font-bold text-[#1d2121] mb-6 flex items-center gap-3">
                <Receipt className="h-6 w-6 text-[#0da5e1]" />
                Invoicing Features
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <Receipt className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Invoice Generation</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Create professional invoices with customizable templates, automated calculations, and tax handling.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <CreditCard className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Payment Tracking</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Track invoice payments, manage overdue accounts, and send automated payment reminders.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <BarChart3 className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Financial Reports</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Generate comprehensive financial reports, revenue analytics, and cash flow projections.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'pl':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Profit & Loss (P&L)
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Comprehensive financial performance analysis with detailed P&L statements, revenue tracking, and expense management.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50/50 p-8 rounded-2xl border border-gray-200/50 shadow-sm">
              <h4 className="text-2xl font-montserrat font-bold text-[#1d2121] mb-6 flex items-center gap-3">
                <TrendingUp className="h-6 w-6 text-[#0da5e1]" />
                P&L Features
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Revenue Analysis</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Track revenue streams, analyze growth trends, and identify profitable business segments.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <Calculator className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Expense Management</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Categorize and track expenses, monitor cost centers, and optimize spending patterns.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <BarChart3 className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Profitability Reports</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Generate detailed P&L statements, margin analysis, and profitability reports for informed decision making.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'payment-requests':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Payment Requests
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Manage payment requests and approvals with streamlined workflows, approval chains, and financial tracking.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50/50 p-8 rounded-2xl border border-gray-200/50 shadow-sm">
              <h4 className="text-2xl font-montserrat font-bold text-[#1d2121] mb-6 flex items-center gap-3">
                <CreditCard className="h-6 w-6 text-[#0da5e1]" />
                Payment Request Features
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <CreditCard className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Request Management</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Create, submit, and track payment requests with detailed documentation and approval workflows.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <UserCheck className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Approval Workflows</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Configure approval chains, set spending limits, and automate approval processes based on amount and category.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <BarChart3 className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Financial Tracking</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Track payment history, monitor spending patterns, and generate financial reports for better budget management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'leads':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Lead Management
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Track and convert leads to clients with comprehensive lead management, pipeline tracking, and conversion analytics.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50/50 p-8 rounded-2xl border border-gray-200/50 shadow-sm">
              <h4 className="text-2xl font-montserrat font-bold text-[#1d2121] mb-6 flex items-center gap-3">
                <FileText className="h-6 w-6 text-[#0da5e1]" />
                Lead Management Features
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <FileText className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Lead Capture</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Capture leads from multiple sources, organize contact information, and track lead sources and quality.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <Users className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Pipeline Management</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Manage sales pipeline, track lead progression, and monitor conversion rates at each stage.
                  </p>
                </div>

                <div className="bg-white/80 p-6 rounded-xl border border-gray-200/50 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#0da5e1]/10 to-[#00a979]/10 rounded-lg">
                      <BarChart3 className="h-6 w-6 text-[#0da5e1]" />
                    </div>
                    <h5 className="text-lg font-montserrat font-semibold text-[#1d2121]">Conversion Analytics</h5>
                  </div>
                  <p className="text-sm font-roboto text-gray-700 leading-relaxed">
                    Analyze lead conversion rates, identify successful strategies, and optimize sales processes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'hrms':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-montserrat font-bold text-[#1d2121] mb-4">
                Human Resource Management System
              </h3>
              <p className="font-roboto text-[#1d2121] leading-relaxed text-lg max-w-3xl mx-auto">
                Comprehensive HR management system with attendance tracking, KRA management,
                and performance monitoring for optimal team productivity.
              </p>
            </div>

            <div className="bg-gradient-to-r from-[#00a979]/10 to-[#0da5e1]/10 p-8 rounded-2xl">
              <h4 className="text-2xl font-montserrat font-semibold text-[#1d2121] mb-6 text-center">HRMS Features</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <UserCheck className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Attendance Tracking</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Daily attendance monitoring with punctuality and consistency metrics</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <BarChart3 className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">KRA Management</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Key Result Areas tracking with progress monitoring and goal setting</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <Users className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Team Management</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Team oversight with performance comparisons and management tools</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <Calendar className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Performance Reviews</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Regular performance reviews with feedback and improvement tracking</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#00a979]/15 to-[#00a979]/8 p-3 rounded-lg w-fit mb-4">
                    <Zap className="h-6 w-6 text-[#00a979]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#00a979] mb-3 text-lg">Email Automation</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Automated email reports for attendance and KRA progress</p>
                </div>
                <div className="bg-white/90 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-br from-[#0da5e1]/15 to-[#0da5e1]/8 p-3 rounded-lg w-fit mb-4">
                    <Building2 className="h-6 w-6 text-[#0da5e1]" />
                  </div>
                  <h5 className="font-montserrat font-semibold text-[#0da5e1] mb-3 text-lg">Analytics Dashboard</h5>
                  <p className="font-roboto text-sm text-gray-700 leading-relaxed">Comprehensive analytics for team performance and productivity insights</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-montserrat font-bold text-[#1d2121] mb-4">
              {sections.find(s => s.id === selectedSection)?.title || 'Section'}
            </h3>
            <p className="font-roboto text-[#1d2121] leading-relaxed text-lg">
              This section is under development. Please check back soon for comprehensive documentation.
            </p>
            <div className="bg-gradient-to-r from-[#00a979]/10 to-[#0da5e1]/10 p-6 rounded-xl">
              <h4 className="text-xl font-montserrat font-semibold text-[#1d2121] mb-2">Coming Soon</h4>
              <p className="font-roboto text-gray-700">
                We're working on detailed documentation for this section. It will include step-by-step guides,
                best practices, and comprehensive workflows.
              </p>
            </div>
          </div>
        );
    }
  };

  // Add/remove body class to hide attendance widget when manual is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.classList.add('user-manual-open');
    } else {
      document.body.classList.remove('user-manual-open');
    }

    return () => {
      document.body.classList.remove('user-manual-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 lg:left-72 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-2 sm:p-4" style={{ zIndex: 9999 }}>
      <Card className="w-full max-w-7xl h-[95vh] sm:h-[90vh] bg-white/95 backdrop-blur-xl border-0 shadow-2xl overflow-hidden flex flex-col">
        <CardHeader className="border-b border-gray-200/50 bg-gradient-to-r from-white to-gray-50/30 backdrop-blur-sm p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-montserrat font-bold text-[#1d2121] truncate">CupShup AI User Manual</h2>
              <p className="text-xs sm:text-sm font-roboto text-gray-600 mt-1">For {userFunction} Function</p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <Button
                onClick={generatePDF}
                className="bg-gradient-to-r from-[#00a979] to-[#0da5e1] hover:from-[#00a979]/90 hover:to-[#0da5e1]/90 text-white font-montserrat font-semibold text-xs sm:text-sm px-2 sm:px-4 py-1 sm:py-2"
              >
                <Download className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Download PDF</span>
                <span className="sm:hidden">PDF</span>
              </Button>
              <Button
                variant="ghost"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-1 sm:p-2"
              >
                <Menu className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <Button variant="ghost" onClick={onClose} className="p-1 sm:p-2">
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <div className="flex h-full overflow-hidden">
          {/* Sidebar */}
          <div className={`${isSidebarOpen ? 'block' : 'hidden'} lg:block w-full sm:w-80 lg:w-64 border-r border-gray-200/50 bg-gradient-to-b from-gray-50/50 to-white/80 absolute lg:relative z-[10000] lg:z-auto overflow-y-auto`} style={{ height: 'calc(100% - 80px)', top: '80px' }}>
            <div className="p-3 sm:p-4">
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search manual..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0da5e1]/20 focus:border-[#0da5e1] bg-white/80"
                />
              </div>

              {/* Navigation */}
              <nav className="space-y-2">
                {sections
                  .filter(section =>
                    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    section.description.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((section) => (
                    <button
                      key={section.id}
                      onClick={() => {
                        setSelectedSection(section.id);
                        setIsSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-200 ${selectedSection === section.id
                          ? 'bg-gradient-to-r from-[#0da5e1]/15 to-[#0da5e1]/8 border border-[#0da5e1]/20 text-[#0da5e1] shadow-sm'
                          : 'hover:bg-white/60 text-gray-700 hover:text-[#1d2121]'
                        }`}
                    >
                      <section.icon className="h-5 w-5 flex-shrink-0" />
                      <div className="text-left">
                        <div className="font-montserrat font-semibold text-sm">
                          {section.title}
                        </div>
                        <div className="font-roboto text-xs text-gray-500 line-clamp-1">
                          {section.description}
                        </div>
                      </div>
                    </button>
                  ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <CardContent className="p-4 sm:p-6 lg:p-8 h-full">
              <div className="max-w-5xl mx-auto h-full">
                {/* Breadcrumb */}
                <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-roboto text-gray-500 mb-4 sm:mb-6">
                  <span className="truncate">{userFunction} Function</span>
                  <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="text-[#0da5e1] truncate">
                    {sections.find(s => s.id === selectedSection)?.title}
                  </span>
                </div>

                {/* Content */}
                {renderContent()}
              </div>
            </CardContent>
          </div>
        </div>
      </Card>
    </div>
  );
};

export { SimpleUserManual };
export default SimpleUserManual;